import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Input } from '@app/form';
import { SquareButton } from '@app/core';
import { toEuros } from 'lib/numbers';
import { ChangeEvent } from 'preact/compat';
import { AddQuoteProductLine } from './AddQuoteProductLine';
import { QuoteLine, lineTotal, sumLines, VAT_RATE } from './types';

const useStyles = createUseStyles((theme: Theme) => ({
  table: {
    width: '100%',
    borderSpacing: `0 ${theme.marginBase}px`,
    borderCollapse: 'separate',
    textAlign: 'right',

    '& tbody tr:nth-child(even)': {
      backgroundColor: theme.colors.grayBackground,
    },
    '& td, & th': {
      padding: [[theme.marginBase, theme.marginBase * 2]],
    },
  },
  titleLine: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
    textAlign: 'right',
    '& th': {
      borderBottom: {
        color: theme.colors.grayBorder,
        style: 'solid',
        width: 1,
      },
    },
  },
  left: {
    textAlign: 'left',
  },
  line: {
    ...theme.fonts.caption,
    color: theme.colors.inkPrimary,
  },
  price: {
    ...theme.fonts.caption,
    textAlign: 'center',
    width: 120,
  },
  totalLine: {
    ...theme.fonts.label,
  },
}));

interface Props {
  lines: QuoteLine[];
  duration: number;
  numberOfParticipants: number;
  locale: string;
  vat: boolean;
  onChange: (lines: QuoteLine[]) => void;
}

export const QuoteLinesTable = ({
  lines,
  duration,
  numberOfParticipants,
  locale,
  vat,
  onChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const updateLine = useCallback(
    (id: string, patch: Partial<QuoteLine>) =>
      onChange(
        lines.map((line) => (line.id === id ? { ...line, ...patch } : line)),
      ),
    [lines, onChange],
  );

  const removeLine = useCallback(
    (id: string) => onChange(lines.filter((line) => line.id !== id)),
    [lines, onChange],
  );

  const addLine = useCallback(
    (line: QuoteLine) => onChange([...lines, line]),
    [lines, onChange],
  );

  const total = sumLines(lines);
  const taxes = vat ? Math.round(total * VAT_RATE) : 0;

  return (
    <table className={classes.table}>
      <thead>
        <tr className={classes.titleLine}>
          <th className={classes.left}>{t('admin.billing.table.product')}</th>
          <th>{t('admin.billing.table.price')}</th>
          <th>{t('admin.billing.table.quantity')}</th>
          <th>{t('admin.billing.table.total')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.id} className={classes.line}>
            <td className={classes.left}>
              <Input
                noMargin
                value={line.title}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  updateLine(line.id, {
                    title: (event.currentTarget || event.target).value,
                    edited: true,
                  })
                }
              />
            </td>
            <td className={classes.price}>
              <Input
                noMargin
                type="number"
                value={line.unitPrice / 100}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  updateLine(line.id, {
                    unitPrice: Math.round(
                      Number((event.currentTarget || event.target).value) * 100,
                    ),
                    edited: true,
                  })
                }
              />
            </td>
            <td className={classes.price}>
              <Input
                noMargin
                type="number"
                value={line.quantity}
                disabled={!!line.pricePerParticipant}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  updateLine(line.id, {
                    quantity:
                      Number((event.currentTarget || event.target).value) || 0,
                  })
                }
              />
            </td>
            <td>{toEuros(lineTotal(line))}</td>
            <td>
              <SquareButton
                icon="ri-delete-bin-line"
                color="red"
                small
                variant="transparent"
                onClick={() => removeLine(line.id)}
              />
            </td>
          </tr>
        ))}

        <AddQuoteProductLine
          duration={duration}
          numberOfParticipants={numberOfParticipants}
          locale={locale}
          onAdd={addLine}
        />

        <tr className={classes.totalLine}>
          <td className={classes.left}>{t('quote.quote.totalNoTax')}</td>
          <td />
          <td />
          <td>{toEuros(total)}</td>
          <td />
        </tr>
        {vat && (
          <tr className={classes.line}>
            <td className={classes.left}>
              {t('quote.quote.vat', { rate: VAT_RATE * 100 })}
            </td>
            <td />
            <td />
            <td>{toEuros(taxes)}</td>
            <td />
          </tr>
        )}
        <tr className={classes.totalLine}>
          <td className={classes.left}>{t('quote.quote.totalWithTax')}</td>
          <td />
          <td />
          <td>{toEuros(total + taxes)}</td>
          <td />
        </tr>
      </tbody>
    </table>
  );
};
